import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, RotateCcw, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { getNetworkDrafts, removeNetworkDraft, clearNetworkDrafts } from '../../utils/networkDrafts';
import { safeCopyToClipboard } from '../../utils/clipboard';

/**
 * NetworkDraftBanner
 * A dismissible banner that appears when there are locally-stashed drafts
 * left over from a failed /api/generate call (no network / timeout).
 * Lets the user restore the notes into the current input, or copy them out.
 */
const NetworkDraftBanner = ({ onRestore }) => {
  const [drafts, setDrafts] = useState(() => getNetworkDrafts());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const refresh = () => setDrafts(getNetworkDrafts());
    window.addEventListener('writeright:network-drafts-changed', refresh);
    window.addEventListener('online', refresh);
    return () => {
      window.removeEventListener('writeright:network-drafts-changed', refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);

  const restore = useCallback((d) => {
    if (onRestore) onRestore(d);
    removeNetworkDraft(d.id);
    toast.success('Draft restored — try Generate again');
  }, [onRestore]);

  const copy = useCallback((d) => {
    safeCopyToClipboard(d.notes);
    toast.success('Draft copied to clipboard');
  }, []);

  if (dismissed || !drafts || drafts.length === 0) return null;

  const latest = drafts[0];
  const preview = (latest.notes || '').trim().slice(0, 80);

  return (
    <div
      className="sticky top-0 z-40 mx-2 mt-2 rounded-lg border border-amber-500/40 bg-amber-950/85 px-3 py-2 shadow-lg"
      data-testid="network-draft-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-amber-300">
            Connection dropped — {drafts.length} draft{drafts.length > 1 ? 's' : ''} saved locally
          </p>
          <p className="text-[11px] text-amber-200/80 truncate mt-0.5">
            "{preview}{latest.notes.length > 80 ? '…' : ''}"
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <button
              onClick={() => restore(latest)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 transition-colors"
              data-testid="network-draft-restore-btn"
            >
              <RotateCcw className="w-3 h-3" />
              Restore & Retry
            </button>
            <button
              onClick={() => copy(latest)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-secondary/60 border border-border text-foreground/80 hover:bg-secondary transition-colors"
              data-testid="network-draft-copy-btn"
            >
              <Copy className="w-3 h-3" />
              Copy
            </button>
            {drafts.length > 1 && (
              <span className="text-[10px] text-amber-300/70">
                +{drafts.length - 1} more draft{drafts.length - 1 > 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={() => { clearNetworkDrafts(); toast.info('All network drafts cleared'); }}
              className="ml-auto text-[10px] text-amber-300/60 hover:text-amber-300 underline"
              data-testid="network-draft-clear-btn"
            >
              Clear all
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-amber-300/60 hover:text-amber-300 rounded"
          title="Dismiss (drafts remain saved)"
          data-testid="network-draft-dismiss-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default NetworkDraftBanner;
